import { PRICE_SOURCE_LABELS, formatILS, israelDay, addDaysToDay, type LineItemInput } from '@takumi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Badge, Dialog, DialogContent, Spinner } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useInvalidateCrm } from '@/lib/queries';
import type { PricedDocument, Product } from '@/lib/sales-types';
import type { Contact } from '@/lib/types';
import { cn } from '@/lib/utils';

interface Row {
  key: string;
  productId: string;
  description: string;
  quantity: string;
  discountPct: string;
  manualPrice: string;
  overrideReason: string;
}

const newRow = (): Row => ({ key: Math.random().toString(36).slice(2), productId: '', description: '', quantity: '1', discountPct: '0', manualPrice: '', overrideReason: '' });

export function useProducts() {
  return useQuery({ queryKey: ['products'], queryFn: () => api.get<Product[]>('/products'), staleTime: 60_000 });
}

function toItems(rows: Row[]): LineItemInput[] {
  return rows
    .filter((r) => r.productId || r.description.trim())
    .map((r) => ({
      productId: r.productId || null,
      description: r.description.trim() || null,
      quantity: Number(r.quantity) || 0,
      discountPct: Number(r.discountPct) || 0,
      unitPrice: r.manualPrice === '' ? null : Number(r.manualPrice),
      overrideReason: r.overrideReason.trim() || null,
    }));
}

/**
 * Quote / order editor. Prices are never computed only in the browser: every change is priced by the server
 * (customer-specific pricing, tiers, VAT) and the explanation is shown per line.
 */
export function DocumentEditorDialog({
  kind,
  open,
  onOpenChange,
  organizationId,
  organizationName,
  contacts,
}: {
  kind: 'quote' | 'order';
  open: boolean;
  onOpenChange: (o: boolean) => void;
  organizationId: string;
  organizationName: string;
  contacts: Pick<Contact, 'id' | 'fullName' | 'isPrimary'>[];
}) {
  const navigate = useNavigate();
  const invalidate = useInvalidateCrm();
  const products = useProducts();
  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [contactId, setContactId] = useState('');
  const [shipping, setShipping] = useState('0');
  const [docDiscount, setDocDiscount] = useState('0');
  const [notes, setNotes] = useState('');
  const [validUntil, setValidUntil] = useState(addDaysToDay(israelDay(), 14));
  const [orderDate, setOrderDate] = useState(israelDay());
  const [preview, setPreview] = useState<PricedDocument | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [pricing, setPricing] = useState(false);

  useEffect(() => {
    if (open) {
      setRows([newRow()]);
      setContactId(contacts.find((c) => c.isPrimary)?.id ?? '');
      setShipping('0');
      setDocDiscount('0');
      setNotes('');
      setValidUntil(addDaysToDay(israelDay(), 14));
      setOrderDate(israelDay());
      setPreview(null);
    }
  }, [open, contacts]);

  const items = useMemo(() => toItems(rows), [rows]);
  const payloadKey = JSON.stringify({ items, shipping, docDiscount });

  // Debounced server-side pricing preview.
  useEffect(() => {
    if (!open || !items.length || items.some((i) => i.quantity <= 0)) {
      setPreview(null);
      return;
    }
    setPricing(true);
    const t = setTimeout(async () => {
      try {
        const p = await api.post<PricedDocument>('/pricing/preview', {
          organizationId, items, shipping: Number(shipping) || 0, documentDiscountPct: Number(docDiscount) || 0,
        });
        setPreview(p);
        setPreviewError(null);
      } catch (e) {
        setPreviewError(errorMessage(e));
      } finally {
        setPricing(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, open, organizationId]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        organizationId,
        contactId: contactId || null,
        items,
        shipping: Number(shipping) || 0,
        documentDiscountPct: Number(docDiscount) || 0,
        notes: notes.trim() || null,
        ...(kind === 'quote' ? { validUntil } : { orderDate }),
      };
      return api.post<{ id: string; number: string }>(kind === 'quote' ? '/quotes' : '/orders', body);
    },
    onSuccess: (doc) => {
      invalidate(organizationId);
      toast.success(`${kind === 'quote' ? 'הצעת מחיר' : 'הזמנה'} ${doc.number} נוצרה`);
      onOpenChange(false);
      navigate(kind === 'quote' ? `/quotes/${doc.id}` : `/orders/${doc.id}`);
    },
    onError: (e) => toast.error(e instanceof ApiError && e.issues.length ? e.issues.map((i) => i.message).join(' · ') : errorMessage(e)),
  });

  const update = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const productMap = new Map(products.data?.map((p) => [p.id, p]));
  // Rows without product/description are not sent for pricing; map each row to its priced line.
  let pricedIdx = -1;
  const lineIndexOfRow = rows.map((r) => (r.productId || r.description.trim() ? ++pricedIdx : -1));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!items.length) return toast.error('יש להוסיף לפחות פריט אחד');
    save.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={kind === 'quote' ? 'הצעת מחיר חדשה' : 'הזמנה חדשה'} description={organizationName} wide className="max-w-5xl">
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="איש קשר">
              {(id) => (
                <Select id={id} value={contactId} onChange={(e) => setContactId(e.target.value)}>
                  <option value="">—</option>
                  {contacts.map((c) => <option key={c.id} value={c.id}>{c.fullName}</option>)}
                </Select>
              )}
            </Field>
            {kind === 'quote' ? (
              <Field label="בתוקף עד">{(id) => <Input id={id} type="date" value={validUntil} min={israelDay()} onChange={(e) => setValidUntil(e.target.value)} />}</Field>
            ) : (
              <Field label="תאריך הזמנה">{(id) => <Input id={id} type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />}</Field>
            )}
          </div>

          <div className="overflow-x-auto rounded-card border border-line">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-sunken/80 text-xs font-bold text-muted">
                <tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-start">
                  <th className="w-[32%] ps-3">מוצר / תיאור</th>
                  <th className="w-20">כמות</th>
                  <th className="w-36">מחיר ליחידה</th>
                  <th className="w-20">הנחה %</th>
                  <th className="w-28 text-end">סה״כ שורה</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line/70">
                {rows.map((r, i) => {
                  const line = lineIndexOfRow[i]! >= 0 ? preview?.lines[lineIndexOfRow[i]!] : undefined;
                  const product = productMap.get(r.productId);
                  const overridden = r.manualPrice !== '' && line && Number(r.manualPrice) !== line.resolvedPrice;
                  return (
                    <tr key={r.key} className="align-top">
                      <td className="py-2 ps-3 pe-2">
                        <Select
                          aria-label={`מוצר בשורה ${i + 1}`}
                          value={r.productId}
                          onChange={(e) => update(r.key, { productId: e.target.value, manualPrice: '', overrideReason: '' })}
                        >
                          <option value="">פריט חופשי…</option>
                          {products.data?.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                        </Select>
                        {!r.productId && (
                          <Input className="mt-1.5" placeholder="תיאור הפריט" aria-label={`תיאור שורה ${i + 1}`} value={r.description} onChange={(e) => update(r.key, { description: e.target.value })} />
                        )}
                        {product && product.trackInventory && product.onHand !== null && Number(r.quantity) > Number(product.onHand) && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-today"><AlertTriangle className="size-3" /> במלאי {product.onHand} בלבד</p>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <Input type="number" min={0} step="0.001" aria-label={`כמות שורה ${i + 1}`} value={r.quantity} onChange={(e) => update(r.key, { quantity: e.target.value })} />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          aria-label={`מחיר שורה ${i + 1}`}
                          placeholder={line ? String(line.resolvedPrice) : ''}
                          value={r.manualPrice}
                          onChange={(e) => update(r.key, { manualPrice: e.target.value })}
                        />
                        {line && (
                          <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-muted">
                            <Badge variant={line.priceSource === 'manual' ? 'today' : line.priceSource === 'customer' || line.priceSource === 'contract' ? 'gold' : 'neutral'}>
                              {PRICE_SOURCE_LABELS[line.priceSource]}
                            </Badge>
                            {line.priceSource !== 'manual' && line.explanation}
                          </div>
                        )}
                        {r.productId && (r.manualPrice !== '' || overridden) && (
                          <Input className="mt-1.5" placeholder="סיבת שינוי המחיר (חובה)" aria-label={`סיבת שינוי מחיר שורה ${i + 1}`} value={r.overrideReason} onChange={(e) => update(r.key, { overrideReason: e.target.value })} />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <Input type="number" min={0} max={100} step="0.5" aria-label={`הנחה שורה ${i + 1}`} value={r.discountPct} onChange={(e) => update(r.key, { discountPct: e.target.value })} />
                      </td>
                      <td className="px-2 py-2 text-end font-semibold tabular">{line ? formatILS(line.lineTotal) : '—'}</td>
                      <td className="py-2 pe-2">
                        <Button variant="ghost" size="icon-sm" aria-label="מחיקת שורה" disabled={rows.length === 1} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                          <Trash2 />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-line p-2">
              <Button variant="ghost" size="sm" onClick={() => setRows((rs) => [...rs, newRow()])}>
                <Plus /> הוספת שורה
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_320px]">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="הנחה כללית %">{(id) => <Input id={id} type="number" min={0} max={100} step="0.5" value={docDiscount} onChange={(e) => setDocDiscount(e.target.value)} />}</Field>
              <Field label="משלוח (₪, לפני מע״מ)">{(id) => <Input id={id} type="number" min={0} step="1" value={shipping} onChange={(e) => setShipping(e.target.value)} />}</Field>
              <Field label="הערות" className="sm:col-span-2">{(id) => <Textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />}</Field>
            </div>
            <div className={cn('rounded-card border border-line bg-sunken/50 p-4 text-sm', pricing && 'opacity-70')}>
              {previewError && <p className="mb-2 text-xs text-overdue">{previewError}</p>}
              {preview ? (
                <dl className="flex flex-col gap-1.5 tabular">
                  <div className="flex justify-between"><dt>סכום ביניים</dt><dd>{formatILS(preview.totals.subtotal)}</dd></div>
                  {preview.totals.discountTotal > 0 && <div className="flex justify-between text-success"><dt>הנחות</dt><dd>-{formatILS(preview.totals.discountTotal)}</dd></div>}
                  {preview.totals.shipping > 0 && <div className="flex justify-between"><dt>משלוח</dt><dd>{formatILS(preview.totals.shipping)}</dd></div>}
                  <div className="flex justify-between"><dt>מע״מ {Math.round(preview.vatRate * 100)}%</dt><dd>{formatILS(preview.totals.vatAmount)}</dd></div>
                  <div className="mt-1 flex justify-between border-t border-line-strong pt-2 text-base font-bold"><dt>סה״כ</dt><dd>{formatILS(preview.totals.total)}</dd></div>
                </dl>
              ) : (
                <div className="flex items-center justify-center gap-2 py-6 text-muted">{pricing ? <Spinner /> : 'בחרו מוצרים לחישוב'}</div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" variant={kind === 'quote' ? 'primary' : 'gold'} loading={save.isPending}>
              {kind === 'quote' ? 'יצירת הצעת מחיר' : 'יצירת הזמנה'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
